import React from 'react';
import { ArrowLeftIcon, CalendarIcon, CheckCircle2Icon, Clock3Icon, FuelIcon, MapPinIcon, CircleParkingIcon, RouteIcon, SearchIcon, ShieldCheckIcon, UserRoundCheckIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FleetSection } from '../components/FleetSection';
import { services } from '../data/services';
import type { TripSearch } from '../types/search';
interface SearchResultsPageProps {
  tripSearch: TripSearch;
}
export function SearchResultsPage({
  tripSearch
}: SearchResultsPageProps) {
  const service = services.find(item => item.id === tripSearch.service)!;
  const ServiceIcon = service.icon;
  const isCityTour = tripSearch.service === 'citytour';
  const routeInfo = getRouteInfo(tripSearch.pickup, tripSearch.drop);
  const scheduleLabel = isCityTour ? `${tripSearch.cityTourDuration === 'half-day' ? 'Half day' : 'Full day'} city tour` : tripSearch.shift === 'day' ? 'Day travel · 06:00–20:00' : 'Night travel · 20:00–06:00';
  return <main className="bg-slate-50" data-id="element-403" data-mp="2gtzjhxhg7mnd">
      <div className="border-b border-slate-200 bg-white" data-id="element-404" data-mp="2gt6htaxcjnc3">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6" data-id="element-405" data-mp="2buvap7yfrqxk">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-brand-700 hover:text-brand-800" data-id="element-406" data-mp="2pnl0lnh35fzy">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-407" data-mp="2lm9h7zot1r07" /> Back to home
          </Link>
          <a href="/#book" className="inline-flex items-center gap-2 text-sm font-semibold text-ink-soft hover:text-brand-700" data-id="element-408" data-mp="1xx6pfz0gihrz">
            <SearchIcon className="h-4 w-4" data-id="element-409" data-mp="1sywthm6wrd5b" /> Edit search
          </a>
        </div>
      </div>

      <section className="w-full bg-white py-8 sm:py-10" data-id="element-410" data-mp="26yem6cnbrajy">
        <div className="mx-auto max-w-6xl px-4 sm:px-6" data-id="element-411" data-mp="21x8soclubj9d">
          <div className="flex flex-col gap-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" data-id="element-412" data-mp="21zv0mdcn80s4">
            <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start" data-id="element-413" data-mp="3dcxh78eqsqca">
              <div data-id="element-414" data-mp="3df9sx3twq4gw">
                <div className="flex items-center gap-2 text-sm font-bold text-brand-700" data-id="element-415" data-mp="2azrdy33ph76a">
                  <ServiceIcon className="h-4 w-4" data-id="element-416" data-mp="2fyc5le4w6ij4" /> {service.label}
                </div>
                <h1 className="mt-2 font-display text-2xl font-800 text-ink sm:text-3xl" data-id="element-417" data-mp="2k064bgvj1cgn">
                  {isCityTour ? `${tripSearch.pickup} city tour` : <>{tripSearch.pickup} <span className="mx-1 text-brand-500" data-id="element-418" data-mp="2oyqvyrwpqnth">→</span> {tripSearch.drop}</>}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-ink-soft" data-id="element-419" data-mp="1saums83luusa">
                  {isCityTour ? 'Compare cars for your city tour. Your driver stays flexible for the stops you choose.' : 'Compare cars for your one-way journey. Every option below includes a professional driver and the services listed for that vehicle.'}
                </p>
              </div>
              <div className="rounded-xl bg-brand-50 px-4 py-3 text-sm text-brand-900" data-id="element-420" data-mp="18kqzjqx2tgaz">
                <p className="font-bold" data-id="element-421" data-mp="1diiwizckvsm1">{scheduleLabel}</p>
                <p className="mt-0.5 text-xs text-brand-700" data-id="element-422" data-mp="16148umn63q24">
                  {tripSearch.date ? `${isCityTour ? 'Tour starts' : 'Pickup'}: ${tripSearch.date}${tripSearch.time ? ` at ${tripSearch.time}` : ''}` : 'Select a date and time during booking'}
                </p>
              </div>
            </div>

            <div className="grid gap-3 border-y border-slate-100 py-4 sm:grid-cols-3" data-id="element-423" data-mp="s2w8s5uu48mq">
              <RouteFact icon={<RouteIcon className="h-5 w-5" data-id="element-425" data-mp="n3tar5d9v2o9" />} label="Estimated route" value={routeInfo.distance} data-id="element-424" data-mp="n31hi175dh15" />
              <RouteFact icon={<Clock3Icon className="h-5 w-5" data-id="element-427" data-mp="dbx5nacrqcaj" />} label="Drive time" value={routeInfo.duration} data-id="element-426" data-mp="i97054f0le58" />
              <RouteFact icon={<MapPinIcon className="h-5 w-5" data-id="element-429" data-mp="8dcc1ts0uqv2" />} label="Pickup & drop" value="Door-to-door service" data-id="element-428" data-mp="8cub4juezogt" />
            </div>

            <div data-id="element-430" data-mp="4a7co038n5an">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500" data-id="element-431" data-mp="4apjfqnj11wt">Included with your booking</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-432" data-mp="3vjmston94m7f">
                <IncludedService icon={<UserRoundCheckIcon className="h-5 w-5" data-id="element-434" data-mp="1zgrjlaey38sj" />} title="Experienced driver" text="Verified local driver assigned" data-id="element-433" data-mp="1zex96q1auz2p" />
                <IncludedService icon={<FuelIcon className="h-5 w-5" data-id="element-436" data-mp="1pnj3u1mo351n" />} title="Fuel included" text="Covered within your selected car fare" data-id="element-435" data-mp="1pou9y83l4ovx" />
                <IncludedService icon={<CircleParkingIcon className="h-5 w-5" data-id="element-438" data-mp="1ulkk6espkq9f" />} title="Parking included" text="No surprise parking costs" data-id="element-437" data-mp="1um44g25tzqqm" />
                <IncludedService icon={<ShieldCheckIcon className="h-5 w-5" data-id="element-440" data-mp="1kq0tq7wwyjs2" />} title="Ride protection" text="Assisted, safe journey support" data-id="element-439" data-mp="1kphmpm8sxfst" />
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-soft" data-id="element-441" data-mp="16raxsvltyq5a"><CheckCircle2Icon className="h-4 w-4 text-brand-500" data-id="element-442" data-mp="1bo1bxde2rbo2" /> Per-car km limits, tolls, and extra benefits are shown in each car’s details.</p>
            </div>
          </div>
        </div>
      </section>

      <FleetSection tripSearch={tripSearch} data-id="element-443" data-mp="361soolrpabnq" />
    </main>;
}
function RouteFact({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-3 py-3" data-id="element-444" data-mp="3b87m8wfkvxh4">
      <span className="text-brand-600" data-id="element-445" data-mp="3oz2p9s3ebkcs">{icon}</span>
      <div data-id="element-446" data-mp="3p0njthppn3an">
        <p className="text-xs font-medium text-slate-500" data-id="element-447" data-mp="3p0dttu4o0w4y">{label}</p>
        <p className="text-sm font-bold text-ink" data-id="element-448" data-mp="3k7b6w4ue8kxq">{value}</p>
      </div>
    </div>;
}
function IncludedService({
  icon,
  title,
  text
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
}) {
  return <div className="flex items-start gap-3 rounded-xl bg-brand-50 p-3" data-id="element-449" data-mp="3txdyq1ps3suh">
      <span className="mt-0.5 text-brand-600" data-id="element-450" data-mp="bt2kxfmdmsej">{icon}</span>
      <div data-id="element-451" data-mp="bqge5i7b8gxd">
        <p className="text-sm font-bold text-ink" data-id="element-452" data-mp="bq7yxjnpszj0">{title}</p>
        <p className="mt-0.5 text-xs text-ink-soft" data-id="element-453" data-mp="7rh1tcj91hvw">{text}</p>
      </div>
    </div>;
}
function getRouteInfo(pickup: string, drop: string): {
  distance: string;
  duration: string;
} {
  const normalisedRoute = `${pickup.trim().toLowerCase()}-${drop.trim().toLowerCase()}`;
  const reverseRoute = `${drop.trim().toLowerCase()}-${pickup.trim().toLowerCase()}`;
  if (normalisedRoute === 'kathmandu-pokhara' || reverseRoute === 'kathmandu-pokhara') {
    return {
      distance: 'Approx. 200 km',
      duration: '5–7 hours'
    };
  }
  return {
    distance: 'Confirmed after booking',
    duration: 'Based on your route'
  };
}